<!-- frontend/src/views/SSOSuccess.vue -->
<!-- SSO 登入成功後的處理頁面 -->
<template>
  <div class="sso-success-page">
    <div class="success-container">
      <div class="success-card">
        <!-- 載入中狀態 -->
        <div v-if="isProcessing" class="processing">
          <div class="spinner"></div>
          <h2>正在完成登入...</h2>
          <p>請稍候，系統正在處理您的登入資訊</p>
        </div>

        <!-- 成功狀態 -->
        <div v-else-if="isSuccess" class="success">
          <div class="success-icon">✓</div>
          <h2>登入成功！</h2>
          <p>歡迎回來，{{ userName }}</p>
          <p class="redirect-hint">{{ countdown }} 秒後自動跳轉...</p>
        </div>

        <!-- 錯誤狀態 -->
        <div v-else class="error">
          <div class="error-icon">✕</div>
          <h2>登入失敗</h2>
          <p>{{ errorMessage }}</p>
          <button @click="goToLogin" class="retry-btn">返回登入頁面</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProfile } from '@/api.js'

export default {
  name: 'SSOSuccess',
  data() {
    return {
      isProcessing: true,
      isSuccess: false,
      userName: '',
      errorMessage: '',
      countdown: 3
    }
  },
  mounted() {
    this.processSSOCallback()
  },
  methods: {
    async processSSOCallback() {
      try {
        // 從 URL 取得參數
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token')
        const userId = urlParams.get('user_id')
        const name = urlParams.get('name')
        const error = urlParams.get('error')

        // 檢查是否有錯誤
        if (error) {
          this.handleError(this.getErrorMessage(error))
          return
        }

        // 檢查是否有 token
        if (!token) {
          this.handleError('未收到登入憑證')
          return
        }

        // 先儲存 token
        localStorage.setItem('access_token', token)
        localStorage.setItem('token_type', 'bearer')

        // 呼叫 API 取得完整的用戶資料
        try {
          const res = await getProfile()
          const userData = res.data
          
          // 儲存完整的用戶資訊
          localStorage.setItem('user_info', JSON.stringify({
            user_id: userData.user_id,
            name: userData.name,
            role: userData.role,
            is_authorized: userData.is_authorized || 0,
            dept_id: userData.dept_id,
            dept_name: userData.dept_name,
            email: userData.email,
            login_type: 'sso'
          }))

          this.userName = userData.name || decodeURIComponent(name || userId)
        } catch (profileError) {
          console.warn('Failed to fetch profile, using basic info:', profileError)
          // 如果 API 失敗，使用基本資訊
          localStorage.setItem('user_info', JSON.stringify({
            user_id: userId,
            name: decodeURIComponent(name || userId),
            login_type: 'sso'
          }))
          this.userName = decodeURIComponent(name || userId)
        }

        // 顯示成功訊息
        this.isProcessing = false
        this.isSuccess = true

        // 立即通知 App.vue 更新登入狀態（右上角 header 馬上切換），不等 3 秒倒數
        this.$emit('login-success')

        // 開始倒數
        this.startCountdown()

      } catch (error) {
        console.error('SSO callback processing error:', error)
        this.handleError('處理登入資訊時發生錯誤')
      }
    },

    getErrorMessage(errorCode) {
      const errorMessages = {
        'sso_failed': 'SSO 認證失敗，請重試',
        'invalid_callback': '無效的回調請求',
        'no_token': '無法取得存取權杖',
        'sso_processing_failed': '處理 SSO 登入時發生錯誤'
      }
      return errorMessages[errorCode] || '登入失敗，請重試'
    },

    handleError(message) {
      this.isProcessing = false
      this.isSuccess = false
      this.errorMessage = message
    },

    startCountdown() {
      const timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(timer)
          this.redirectToHome()
        }
      }, 1000)
    },

    redirectToHome() {
      // 檢查是否有之前儲存的頁面
      const redirect = localStorage.getItem('redirect_after_login') || '/'
      localStorage.removeItem('redirect_after_login')

      // 跳轉（login-success 已在 processSSOCallback 立即發出，此處不再重複）
      this.$router.push(redirect)
    },

    goToLogin() {
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.sso-success-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.success-container {
  width: 100%;
  max-width: 450px;
}

.success-card {
  background: white;
  border-radius: 16px;
  padding: 50px 40px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  text-align: center;
}

/* 載入中 */
.processing h2 {
  color: #333;
  margin: 20px 0 10px;
}

.processing p {
  color: #666;
  font-size: 14px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 成功 */
.success-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  font-size: 40px;
  line-height: 80px;
  border-radius: 50%;
  margin: 0 auto 20px;
}

.success h2 {
  color: #28a745;
  margin-bottom: 10px;
}

.success p {
  color: #333;
  font-size: 16px;
  margin-bottom: 5px;
}

.redirect-hint {
  color: #999 !important;
  font-size: 14px !important;
  margin-top: 15px !important;
}

/* 錯誤 */
.error-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  font-size: 40px;
  line-height: 80px;
  border-radius: 50%;
  margin: 0 auto 20px;
}

.error h2 {
  color: #dc3545;
  margin-bottom: 10px;
}

.error p {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
}

.retry-btn {
  padding: 12px 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(102,126,234,0.3);
}
</style>